import { STATS, MEDIA } from "@/data/siteData";
import { Brain, Database, Atom, Layers, Link2, Bot } from "lucide-react";

const PILLARS = [
  { icon: Brain, title: "AI", body: "Agentic systems, foundation models, and production deployments at enterprise scale." },
  { icon: Database, title: "Big Data", body: "Exabyte-scale analytics, data platforms, and the governance that keeps them honest." },
  { icon: Atom, title: "Quantum & Platform Eng.", body: "Quantum-safe networking alongside the platform layer running modern software." },
  { icon: Link2, title: "Blockchain", body: "Distributed trust, tokenised infrastructure, and the regulatory frontier it unlocks." },
  { icon: Bot, title: "Robotics", body: "Embodied intelligence — from warehouse to city — and the compute fabric behind it." },
  { icon: Layers, title: "& More", body: "Edge Computing, IoT, cybersecurity, FinOps, sovereign cloud, developer experience and the policy shaping them." },
];

const VERTICALS = [
  "Software & IT", "FinTech", "Supply Chain & LogisticsTech",
  "FoodTech", "EdTech", "MobilityTech",
  "AgriTech", "Health & MedTech", "EVTech",
  "BioTech", "DefenceTech", "InfraTech",
];

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-y-8 md:gap-8 items-center mb-16">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-[#FF2A00] mb-4">/ 02 — About Us</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase">
              From dialogue
              <br />
              to <span className="text-[#002FA7]">demonstrable</span>
              <br />
              impact.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <p className="text-base text-zinc-800 leading-relaxed">
              <strong>BoT Summit</strong> marks a defining global milestone — shifting
              from dialogue to demonstrable impact. Bringing together leading tech brands
              and visionary industry leaders, the summit serves as a premier platform to
              discuss and showcase cutting-edge advancements in technologies like{" "}
              <span className="text-[#002FA7] font-semibold">AI, Big Data, Quantum
              and Platform Engineering, Blockchain, Robotics, Edge Computing and IoT</span>{" "}
              and more.
            </p>
            <p className="mt-4 text-base text-zinc-800 leading-relaxed">
              It envisions an era where innovation drives inclusive growth and secures
              our vision for a sustainable future for our planet.
            </p>
            <div className="mt-6" data-testid="about-verticals">
              <p className="text-base text-zinc-800 font-semibold">
                Focus on Major technology-driven industry verticals:
              </p>
              <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-[#002FA7] font-medium">
                {VERTICALS.map((v) => (
                  <li key={v} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#FF2A00] shrink-0" />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* From the CEO */}
        <div className="mt-20 md:mt-28 grid grid-cols-12 gap-y-8 md:gap-12 items-start border-t border-zinc-200 pt-16" data-testid="from-the-ceo">
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-[#FF2A00] mb-4">/ Founder's Note</div>
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
              From the
              <br />
              <span className="text-[#002FA7]">CEO.</span>
            </h3>
            <div className="mt-6 h-px w-16 bg-zinc-300" />
            <div className="mt-4 overline text-zinc-500">
              <span className="normal-case">Pierce &amp; Smith</span> · Office of the CEO
            </div>
          </div>

          <div className="col-span-12 md:col-span-8 md:pl-12 md:border-l md:border-zinc-200">
            <div className="font-display text-5xl text-[#002FA7] leading-none mb-2">“</div>
            <div className="space-y-5 text-base text-zinc-800 leading-relaxed">
              <p>
                In today's evolving global landscape we are at a point where
                technologies like <strong>Generative AI, Blockchain, Platform
                Engineering</strong> and more are no longer the conversations of
                the future — rather they are shaping decisions in real time across
                the industry.
              </p>
              <p>
                From my perspective, the real challenge isn't access to
                technology. It's the clarity of knowing what truly matters, what
                to prioritise, and how to lead through constant change.
              </p>
              <p className="font-display text-xl md:text-2xl uppercase tracking-tight text-black">
                That's exactly why we envisioned the <span className="normal-case">BoT</span> Summit.
              </p>
              <p>
                A space that brings together people who are not just adapting to
                the future, but actively building it. This is not about trends —
                it's about direction. It's about making better decisions, faster,
                in a world that won't slow down.
              </p>
              <p className="text-zinc-700">
                I look forward to having you with us.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Bento */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border border-zinc-200">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-${i}`}
              className={`p-6 md:p-8 ${
                i < 3 ? "border-r border-zinc-200" : ""
              } ${i < 2 ? "border-b md:border-b-0" : ""} md:border-b-0`}
            >
              <div className="font-display text-4xl md:text-5xl tracking-tighter">
                {s.value}
              </div>
              <div className="overline text-zinc-500 mt-3">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Focus Areas */}
        <div className="mt-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-zinc-500">On the agenda</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-2">
                Six pillars of the Summit.
              </h3>
            </div>
            <div className="hidden md:block overline text-zinc-400">/ 06 tracks</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              const isLastCol = (i + 1) % 3 === 0;
              const isLastRowLg = i >= 3;
              return (
                <div
                  key={p.title}
                  data-testid={`pillar-${i}`}
                  className={`p-6 md:p-8 bg-white hover:bg-zinc-50 transition-colors relative group
                    ${!isLastCol ? "lg:border-r border-zinc-200" : ""}
                    ${i % 2 === 0 ? "md:border-r lg:border-r" : ""}
                    ${!isLastRowLg ? "lg:border-b" : ""}
                    border-b md:border-b border-zinc-200 last:border-b-0`}
                >
                  <div className="flex items-start justify-between">
                    <Icon size={28} className="text-[#002FA7]" strokeWidth={1.5} />
                    <span className="overline text-zinc-400 text-[10px]">
                      0{i + 1}
                    </span>
                  </div>
                  <h4 className="font-display text-xl mt-6 uppercase tracking-tight">
                    {p.title}
                  </h4>
                  <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Media coverage ticker */}
      <div className="border-t border-zinc-200 bg-white overflow-hidden" data-testid="media-ticker">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-2 flex items-center justify-between">
          <span className="overline text-[#FF2A00]">As featured in</span>
          <span className="overline text-zinc-400 hidden md:inline">Media Coverage</span>
        </div>
        <div className="flex whitespace-nowrap animate-ticker py-5">
          {[...MEDIA, ...MEDIA].map((m, i) => (
            <span
              key={i}
              className="mx-10 font-display text-xl uppercase tracking-tight text-zinc-800 flex items-center gap-10"
            >
              {m}
              <span className="w-1 h-1 bg-zinc-400 rounded-full" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
